import { useState, useEffect, useRef } from 'react';
import { aiPlanning } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Send, Bot, User, Trash2, Plus, MessageSquare, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const res = await aiPlanning.getChatSessions();
      setSessions(res.data);
    } catch (e) {}
  };

  const loadHistory = async (sid) => {
    try {
      const res = await aiPlanning.getChatHistory(sid);
      setMessages(res.data.messages || []);
      setSessionId(sid);
      setShowSessions(false);
    } catch (e) {
      toast.error('Error al cargar historial');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await aiPlanning.chat(userMsg, sessionId);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      if (!sessionId) {
        setSessionId(res.data.session_id);
        loadSessions();
      }
    } catch (e) {
      toast.error('Error del asistente AI');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, ocurrio un error. Intenta de nuevo.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await aiPlanning.deleteChatSession(sid);
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
      toast.success('Conversacion eliminada');
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const quickActions = [
    { label: 'Priorizar mis tareas', prompt: 'Analiza mis tareas pendientes y dime cuales debo priorizar hoy y por que.' },
    { label: 'Plan del dia', prompt: 'Crea un plan de trabajo para hoy basado en mis tareas pendientes y vencidas.' },
    { label: 'Resumen semanal', prompt: 'Dame un resumen del estado de mis tareas y productividad esta semana.' },
    { label: 'Sugerencias', prompt: 'Que mejoras puedo hacer para ser mas productivo basado en mis metricas actuales?' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn h-[calc(100vh-120px)] flex flex-col" data-testid="ai-assistant-page">
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-3">
            <Bot className="h-8 w-8 text-violet-600" />
            Asistente AI
          </h1>
          <p className="text-muted-foreground">Planificacion inteligente y asistencia</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSessions(!showSessions)} data-testid="toggle-sessions-btn">
            <MessageSquare className="h-4 w-4 mr-2" />
            Historial
          </Button>
          <Button variant="outline" size="sm" onClick={startNewChat} data-testid="new-chat-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva
          </Button>
        </div>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Sessions sidebar */}
        {showSessions && (
          <Card className="w-72 flex-shrink-0 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Conversaciones</CardTitle>
            </CardHeader>
            <CardContent className="p-2 overflow-y-auto max-h-[500px]">
              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Sin conversaciones</p>
              ) : (
                sessions.map(s => (
                  <div
                    key={s.session_id}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-muted/50 mb-1 ${sessionId === s.session_id ? 'bg-violet-50 border border-violet-200' : ''}`}
                    onClick={() => loadHistory(s.session_id)}
                    data-testid={`session-${s.session_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{s.last_message}</p>
                      <p className="text-xs text-muted-foreground">{s.message_count} msgs</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={(e) => handleDeleteSession(s.session_id, e)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Chat area */}
        <Card className="flex-1 flex flex-col overflow-hidden">
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Bot className="h-16 w-16 text-violet-300 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Hola! Soy tu asistente AI</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md">
                  Puedo ayudarte a planificar tu dia, priorizar tareas, analizar tu productividad y mas.
                </p>
                <div className="grid grid-cols-2 gap-2 max-w-md">
                  {quickActions.map((qa, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      className="text-xs h-auto py-2 px-3 justify-start"
                      onClick={() => { setInput(qa.prompt); }}
                      data-testid={`quick-action-${idx}`}
                    >
                      {qa.label}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-violet-600" />
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                  )}
                </div>
              ))
            )}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-4 w-4 text-violet-600" />
                </div>
                <div className="bg-muted rounded-xl px-4 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>

          {/* Input area */}
          <div className="p-4 border-t flex-shrink-0">
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu mensaje..."
                disabled={loading}
                className="flex-1"
                data-testid="ai-chat-input"
              />
              <Button type="submit" disabled={loading || !input.trim()} data-testid="ai-send-btn">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}
